import { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import {
  CONSENT_ACCEPTED,
  CONSENT_REJECTED,
  OPEN_COOKIE_PREFERENCES_EVENT,
  getConsentChoice,
  saveConsentChoice,
} from "@/lib/consent";

const choiceButtonClass =
  "inline-flex min-h-11 items-center justify-center rounded-full border border-charcoal bg-transparent px-5 py-3 text-sm font-medium text-charcoal transition hover:bg-charcoal hover:text-sand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2";

export default function CookieConsent() {
  const { t } = useLang();
  const [isOpen, setIsOpen] = useState(() => getConsentChoice() === null);

  useEffect(() => {
    const openPreferences = () => setIsOpen(true);
    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, openPreferences);
    return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, openPreferences);
  }, []);

  const choose = (choice) => {
    saveConsentChoice(choice);
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4 md:p-6" data-testid="cookie-consent-banner">
      <section
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
        className="mx-auto max-w-5xl border border-charcoal/15 bg-sand-50 p-5 shadow-soft md:flex md:items-center md:gap-8 md:p-7"
      >
        <div className="flex-1">
          <p className="mb-2 text-xs uppercase tracking-[0.25em] text-terracotta">
            {t.cookie_consent.overline}
          </p>
          <h2 id="cookie-consent-title" className="font-serif-display text-2xl font-light text-charcoal md:text-3xl">
            {t.cookie_consent.title}
          </h2>
          <p id="cookie-consent-description" className="mt-2 max-w-2xl text-sm leading-relaxed text-charcoal-400">
            {t.cookie_consent.description}
          </p>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 md:mt-0 md:w-80 md:shrink-0">
          <button
            type="button"
            className={choiceButtonClass}
            onClick={() => choose(CONSENT_REJECTED)}
            data-testid="cookie-consent-reject"
          >
            {t.cookie_consent.reject}
          </button>
          <button
            type="button"
            className={choiceButtonClass}
            onClick={() => choose(CONSENT_ACCEPTED)}
            data-testid="cookie-consent-accept"
          >
            {t.cookie_consent.accept}
          </button>
        </div>
      </section>
    </div>
  );
}
